import type { UserOut as IUser } from '@repo/api-codegen/v2';
import type { FormikProps } from 'formik';
import { useMemo } from 'react';
import { useAuthUser } from '../../../api/hooks/authUser';
import {
	useExtendedUserList,
	useGuestUserList,
} from '../../../api/hooks/user/useExtendedUserList';
import { getOwnerAndGroupSelectorOptions } from '../../EntityPageLayout/utils';
import { PropertySelector } from '../../Properties/PropertySelector';
import type { TemplateFormik } from './ManageTemplatePage';

export interface OwnersPropertySelectorProps {
	formik: FormikProps<TemplateFormik>;
}

export function OwnerTemplateSelector({ formik }: OwnersPropertySelectorProps) {
	const { isGuestUser } = useAuthUser();

	const {
		userGroups: groups,
		disabledUsers,
		activeUsers,
		isLoading: isUserLoading,
	} = useExtendedUserList();
	const { usersVisibleToGuests, isLoading: isUsersVisibleToGuestsLoading } =
		useGuestUserList();

	const ownerOptions = useMemo(
		() =>
			isGuestUser
				? ((usersVisibleToGuests ?? []) as unknown as IUser[])
				: [
						...((disabledUsers ?? []) as unknown as IUser[]),
						...((activeUsers ?? []) as unknown as IUser[]),
					],
		[activeUsers, disabledUsers, isGuestUser, usersVisibleToGuests]
	);

	const owners = useMemo(
		() =>
			getOwnerAndGroupSelectorOptions(
				ownerOptions,
				groups ? groups : [],
				(value) => formik.setFieldValue('owners', value),
				(value) => formik.setFieldValue('owners_groups', value)
			).map((owner) => ({
				...owner,
				itemTypeName: 'owners',
			})),
		[groups, ownerOptions, formik]
	);

	return (
		<PropertySelector
			isLoading={isUserLoading || isUsersVisibleToGuestsLoading}
			readOnly={false}
			selected={[
				...(formik.values.owners || []),
				...(formik.values.owners_groups || []),
			]}
			type="multi"
			value="owners"
			iconType="avatar"
			isViewerUser={false}
			options={owners}
			placeholder={'Add owner'}
			placeholderIconName="user"
		/>
	);
}
